/**
 * Pure rules for the SourTrack sync state shown on the opportunity ficha.
 * The backend answers the manual sync with an envelope whose `messageType`, not the HTTP
 * status, carries the outcome; a Warning or an Error must never be presented as a success.
 */
export type SourTrackSyncStatus = 'None' | 'Pending' | 'Integrated' | 'Failed';
export type SourTrackSeverity = 'success' | 'warning' | 'error';

export interface SourTrackIndicator {
  status: SourTrackSyncStatus;
  label: string;
  /** Latest reason as the backend stores it (English today); shown as-is, never translated. */
  detail: string | null;
  tone: 'warning' | 'error' | 'discreet';
}

export interface SourTrackEnvelopeOutcome {
  severity: SourTrackSeverity;
  message: string;
  lead: any | null;
}

const INDICATORS: Record<string, { label: string; tone: SourTrackIndicator['tone'] }> = {
  Pending: { label: 'Pendiente de sincronizar con SourTrack', tone: 'warning' },
  Failed: { label: 'Sincronización con SourTrack fallida', tone: 'error' },
  Integrated: { label: 'Sincronizado con SourTrack', tone: 'discreet' },
};

const FALLBACK_MESSAGES: Record<SourTrackSeverity, string> = {
  success: 'Oportunidad sincronizada con SourTrack.',
  warning: 'No se envió nada a SourTrack.',
  error: 'No se pudo sincronizar con SourTrack.',
};

export function buildSourTrackIndicator(
  status: string | null | undefined,
  detail: string | null | undefined
): SourTrackIndicator | null {
  const entry = status ? INDICATORS[status] : undefined;
  if (!entry) return null;
  return {
    status: status as SourTrackSyncStatus,
    label: entry.label,
    detail: status === 'Integrated' ? null : detail || null,
    tone: entry.tone,
  };
}

/**
 * Pending and Failed offer a retry. Integrated offers a new send too: a quotation approved after the
 * award must reach the same SourTrack project (client decision, 29/10), and SourTrack reuses what it has.
 */
export function canRetrySourTrackSync(status: string | null | undefined, isAwarded: boolean): boolean {
  return isAwarded && (status === 'Pending' || status === 'Failed' || status === 'Integrated');
}

/** Success needs an explicit Success type AND a 2xx status AND no `success: false`; anything else is not one. */
export function classifySyncEnvelope(body: any): SourTrackEnvelopeOutcome {
  const type = String(body?.messageType ?? '').toLowerCase();
  const statusCode = Number(body?.statusCode);
  const failedStatus = !Number.isNaN(statusCode) && statusCode >= 400;
  let severity: SourTrackSeverity;
  if (failedStatus || type === 'error') {
    severity = 'error';
  } else if (type === 'success' && statusCode >= 200 && statusCode < 300 && body?.success !== false) {
    severity = 'success';
  } else {
    severity = 'warning';
  }
  return {
    severity,
    message: body?.message || FALLBACK_MESSAGES[severity],
    lead: body?.data ?? null,
  };
}

/**
 * The quotations that reach SourTrack (client decision, 29/10): in stage Pedido de venta, not
 * cancelled or deleted. The stage decides, not the confirmation date. Same rule as the backend.
 */
export function isSendableQuotation(q: any): boolean {
  return !!q && !q.delete && q.stage === 'SaleOrder' && !q.dateCancelled;
}

export function hasSendableQuotation(quotations: any[] | null | undefined): boolean {
  return (quotations ?? []).some(isSendableQuotation);
}

/**
 * The quotations the user can approve to send: active, not cancelled, not deleted, not yet confirmed,
 * with sale lines. Mirrors the backend rule (active, not cancelled, not deleted, with sale lines) as far as the
 * list allows. The list does not always carry the lines: `linesVerified` says whether the
 * "has sale lines" part could be checked, so the prompt can tell the user when it could not.
 */
export function listCandidateQuotations(quotations: any[] | null | undefined): {
  candidates: any[];
  linesVerified: boolean;
} {
  const all = quotations ?? [];
  const linesVerified = all.length > 0 && all.every((q) => Array.isArray(q?.saleOrderLines));
  const candidates = all.filter(
    (q) =>
      !q?.delete &&
      q?.isActive !== false &&
      q?.stage !== 'Cancelled' &&
      !q?.dateCancelled &&
      // Already confirmed ones cannot be approved again (the backend refuses a second confirmation).
      !q?.dateConfirm &&
      (!linesVerified || q.saleOrderLines.length > 0)
  );
  return { candidates, linesVerified };
}
